import { useCallback, useEffect, useState } from 'react';
import { getErrorMessage, isEligibleContactCaptureCampaign } from '../../helpers';
import { useContactCaptureService } from '../../services';
import { ContactCaptureCampaign } from '../../types';

export const useContactCaptureCampaigns = (
  companyId?: string,
  recordCampaignId?: string | null,
  recordId?: string,
  /** Preselected for a new capture only if it is one of this company's options; never an error. */
  suggestedCampaignId?: string | null,
) => {
  const { addCampaignOption, loadCampaignOptions } = useContactCaptureService();
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [availableCampaigns, setAvailableCampaigns] = useState<ContactCaptureCampaign[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [campaignsError, setCampaignsError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    setCampaignId(recordCampaignId || null);
  }, [companyId, recordId, recordCampaignId]);

  useEffect(() => {
    let active = true;
    setAvailableCampaigns([]);
    setCampaignsError(null);
    setCampaignsLoading(Boolean(companyId));
    if (!companyId) return;
    // Revalidate eligibility whenever a builder opens; the list cache can be stale.
    loadCampaignOptions(companyId, true).then(options => {
      if (!active) return;
      setAvailableCampaigns(options);
      if (suggestedCampaignId && options.some(option => option.id === suggestedCampaignId)) {
        setCampaignId(current => current ?? suggestedCampaignId);
      }
    }).catch(error => {
      if (active) setCampaignsError(getErrorMessage(error, 'Could not load join campaigns.'));
    }).finally(() => {
      if (active) setCampaignsLoading(false);
    });
    return () => { active = false; };
  }, [companyId, loadCampaignOptions, retry, suggestedCampaignId]);

  const campaign = availableCampaigns.find(option => option.id === campaignId) || null;
  const addCampaign = useCallback((created: ContactCaptureCampaign) => {
    if (!isEligibleContactCaptureCampaign(created)) return false;
    setAvailableCampaigns(current => [created, ...current.filter(option => option.id !== created.id)]);
    setCampaignId(created.id);
    setCampaignsError(null);
    if (companyId) addCampaignOption(companyId, created);
    return true;
  }, [addCampaignOption, companyId]);

  return {
    availableCampaigns,
    campaign,
    campaignsError,
    campaignsLoading,
    unavailableCampaign: Boolean(campaignId && !campaign && !campaignsLoading && !campaignsError),
    setCampaign: (selected: ContactCaptureCampaign | null) => setCampaignId(selected?.id || null),
    addCampaign,
    retryCampaigns: () => setRetry(current => current + 1),
  };
};
